import { FlexBox, TextField, theme, trackEvent } from '@rario/shared-components'
import GradientLineLeft from 'assets/svg/GradientLineLeft'
import GradientLineRight from 'assets/svg/GradientLineRight'
import LoginHeader from 'components/LoginHeader'
import LoginSignupForm from 'components/LoginSignupForm'
import Toast from 'components/Toast'
import { GetServerSideProps, NextApiRequest, NextApiResponse, NextComponentType } from 'next/types'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import Head from 'next/head'
import { withAuthMiddleware } from 'utils/withAuthMiddleware'
import { getAccessToken } from 'utils/authMiddleware'
import GoogleLoginButton from 'components/LoginSignupForm/GoogleLoginButton'
import { LoginResponseTypeEnum } from 'interfaces/auth'

const LoginPage: NextComponentType = () => {
  const router = useRouter()

  const redirectUrlSearchParams = new URLSearchParams(router.query as Record<string, string>)
  redirectUrlSearchParams.delete('redirect_uri')
  const redirectUrl = router.query?.redirect_uri
    ? `${router.query?.redirect_uri}?${redirectUrlSearchParams}`
    : '/'

  useEffect(() => {
    trackEvent('Section Viewed', 'track', {
      sub_category: 'login-email_form',
      category: 'login',
      type: 'form',
      view_type: 'default',
    })
    trackEvent('Section Viewed', 'track', {
      sub_category: 'login-social_google',
      category: 'login',
      type: 'form',
      view_type: 'default',
    })
  }, [])

  // Handle Google login errors
  useEffect(() => {
    const error = router.query.error as string
    if (error) {
      let errorMessage = ''

      switch (error) {
        case LoginResponseTypeEnum.USER_NOT_FOUND:
          errorMessage = `Ah! There's no more room, we are currently not accepting new sign-ups. For any assistance please reach out to our support team.`
          break
        case LoginResponseTypeEnum.EMAIL_NOT_FOUND:
          errorMessage = 'Unable to retrieve email from Google account. Please try again.'
          break
        case LoginResponseTypeEnum.INVALID_TOKEN:
          errorMessage = 'Invalid Google authentication token. Please try again.'
          break
        case LoginResponseTypeEnum.AUTHENTICATION_FAILED:
          errorMessage = 'Google authentication failed. Please try again.'
          break
        default:
          errorMessage = 'An error occurred during Google login. Please try again.'
      }

      if (errorMessage) {
        Toast({
          message: errorMessage,
          type: 'error',
        })
        // Clean up the URL by removing error parameters
        const cleanUrl = router.asPath.split('?')[0]
        const otherParams = new URLSearchParams(router.query as Record<string, string>)
        otherParams.delete('error')
        const remainingParams = otherParams.toString()
        const newUrl = remainingParams ? `${cleanUrl}?${remainingParams}` : cleanUrl
        router.replace(newUrl, undefined, { shallow: true })
      }
    }
  }, [router.query.error])

  return (
    <>
      <Head>
        <title> Rario Login</title>
      </Head>
      <LoginHeader />
      <FlexBox px={'15px'} flexDirection={'column'} pt={3}>
        <TextField
          fontSize={'24px'}
          fontWeight={600}
          lineHeight="29px"
          fontFamily={theme.fonts.primary}
          pb={2}
        >
          {'Continue using email'}
        </TextField>
        <GoogleLoginButton redirectUrl={redirectUrl} />
        <FlexBox justifyContent={'center'} alignItems={'center'} flexDirection={'row'} my={5}>
          <GradientLineLeft />
          <TextField
            fontSize={'12px'}
            fontWeight={400}
            lineHeight="16px"
            fontFamily={theme.fonts.secondary}
            pb={0}
            px={3}
            opacity={0.7}
          >
            or
          </TextField>
          <GradientLineRight />
        </FlexBox>
        <LoginSignupForm redirectTo={redirectUrl} />
      </FlexBox>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = withAuthMiddleware({
  getServerSideProps: async ({ req, res }) => {
    let accessTokenResult = null
    let accessToken = null

    try {
      accessTokenResult = await getAccessToken(req as NextApiRequest, res as NextApiResponse)
      accessToken = accessTokenResult.accessToken
      if (accessToken) {
        return {
          redirect: {
            destination: `/`,
            permanent: false,
          },
        }
      }
    } catch (error) {
      // errorCode = 500
    }

    return {
      props: {},
    }
  },
})

export default LoginPage
